import dayjs from 'dayjs'

// The Cost page's words and numbers. Source of truth is reclip-api's
// `api/services/stream/metrics/cost.py` (the daily read) and
// `api/services/stream/controls.py` (the spend gates); keys here are theirs.

// One color per task, fixed in this order and never cycled, from the Stream
// Ingest page's palette so the two pages' blues mean the same kind of thing.
export const TASKS = [
  {
    key: 'transcribe',
    label: 'Transcribe · ElevenLabs',
    color: '#4e79a7',
  },
  { key: 'find_moments', label: 'Find moments · Gemini', color: '#f28e2c' },
  { key: 'judge', label: 'Judge · Gemini', color: '#59a14f' },
  { key: 'route', label: 'Route · Cloud Run', color: '#b07aa1' },
]

export const POOL_COLORS = { ours: '#4e79a7', theirs: '#f28e2c' }

export const CONTROL_WORDS = {
  allowance_mode: 'Daily allowance',
  credit_mode: 'Credit bucket',
  daily_ceiling_hours: 'Daily ceiling',
  unreviewed_mode: 'Unreviewed quotes',
  unreviewed_limit: 'Unreviewed limit',
}

export const GATES = ['allowance_mode', 'credit_mode', 'unreviewed_mode']

export const fmtUsd = (value) =>
  value == null
    ? '—'
    : `$${Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

// Module-level, so the chart's option effect does not re-run every render.
export const fmtUsdAxis = (value) => `$${value}`

export const fmtCredits = (credits) => {
  if (credits == null) return '—'
  if (credits >= 1e6) return `${(credits / 1e6).toFixed(2)}M`
  if (credits >= 1e3)
    return `${Math.round(credits / 1e3).toLocaleString('en-US')}K`
  return `${Math.round(credits)}`
}

// Pool credits as the dollars they are at the contract's rate (`cost.py`
// `cycle.credit_usd`): inside the pool, their share of the prepaid flat fee.
// Null from an API that sends no rate, and the page shows credits alone.
export const poolUsd = (credits, cycle) =>
  credits == null || cycle?.credit_usd == null
    ? null
    : credits * cycle.credit_usd

// A pool figure in dollars with the credits ElevenLabs counts it in after.
export const fmtPool = (credits, cycle) => {
  const usd = poolUsd(credits, cycle)
  return usd == null
    ? `${fmtCredits(credits)} credits`
    : `${fmtUsd(usd)} (${fmtCredits(credits)} credits)`
}

export const fmtPct = (ratio) =>
  ratio == null || !Number.isFinite(ratio) ? '—' : `${Math.round(ratio * 100)}%`

export const fmtHrs = (hours) => {
  if (hours == null) return '—'
  if (hours >= 100) return `${Math.round(hours).toLocaleString('en-US')} h`
  return `${Number(hours).toFixed(1)} h`
}

export const fmtControl = (control, value) => {
  if (control === 'daily_ceiling_hours')
    return value == null ? 'none' : fmtHrs(value)
  if (control === 'unreviewed_limit') return `${value} quotes`
  return value
}

export const sumOf = (rows, pick) =>
  rows.reduce((total, row) => total + (pick(row) || 0), 0)

// The newest day the books hold whole: never today, never a day with no row.
export const latestWhole = (daily) =>
  [...daily].reverse().find(({ books }) => books === 'whole') || null

// The newest Monday-to-Sunday week whose seven days are all whole, or null.
export const latestWholeWeek = (daily) => {
  const byDay = new Map(daily.map((row) => [row.day, row]))
  for (let i = daily.length - 1; i >= 6; i -= 1) {
    const end = new Date(`${daily[i].day}T00:00:00Z`)
    if (end.getUTCDay() !== 0) continue
    const week = []
    for (let back = 6; back >= 0; back -= 1) {
      const day = new Date(end)
      day.setUTCDate(end.getUTCDate() - back)
      week.push(byDay.get(day.toISOString().slice(0, 10)))
    }
    if (week.every((row) => row && row.books === 'whole')) return week
  }
  return null
}

export const dayLabel = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

// The API's datetimes are naive UTC (no zone suffix): read them as UTC, then
// show them on the viewer's clock.
export const fmtWhen = (iso) => {
  if (!iso) return '—'
  const zoned = /(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`
  return dayjs(zoned).format('MMM D, HH:mm')
}

// How long ago a naive-UTC stamp was, in the largest whole unit.
export const fmtAgo = (iso, now = Date.now()) => {
  if (!iso) return null
  const zoned = /(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`
  const minutes = Math.max(
    0,
    Math.round((now - new Date(zoned).getTime()) / 60000)
  )
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`
  return `${Math.round(minutes / 1440)} days ago`
}

export const PROVIDER_WORDS = {
  elevenlabs: 'ElevenLabs',
  vertex: 'Vertex',
  cloud_run: 'Cloud Run',
}

const PROVENANCE_WORDS = {
  contract: 'Contract',
  quoted: 'List price',
  measured: 'Measured',
  reconciled: 'Reconciled',
  assumed: 'Assumed',
}

export const fmtRateFrom = ({ provenance, verified }) =>
  `${PROVENANCE_WORDS[provenance] || provenance} · ${dayLabel(verified)}`

const perMillion = (usd) => `$${(usd * 1e6).toFixed(2)}`

// A task's rate as the price list states it (`cost.py` `by_task[].rate`).
export const fmtRate = ({ task, rate }) => {
  if (task === 'transcribe') return `$${rate.usd.toFixed(2)} an audio hour`
  if (task === 'route')
    return `$${rate.usd} a wall-second (${rate.vcpu} vCPU, ${rate.gib} GiB)`
  return `${perMillion(rate.prompt)} in, ${perMillion(rate.output)} out per M`
}

const millions = (n) => `${(n / 1e6).toFixed(1)}M`

// A task's units in the words the mockup used.
export const fmtUnits = ({ task, units }) => {
  if (task === 'transcribe') return `${fmtHrs(units.audio_hours)} of audio`
  if (task === 'route')
    return `${fmtHrs(units.wall_seconds / 3600)} of router time · ${fmtHrs(
      units.captured_hours
    )} of raw audio`
  const parts = [
    `${millions(units.prompt)} in`,
    `${millions(units.output)} out`,
  ]
  if (units.thinking) parts.push(`${millions(units.thinking)} thinking`)
  return parts.join(' · ')
}
